import Link from 'components/ui/AppLink';
import Loader from 'components/ui/LoadingIndicator';
import ContainerBox from 'components/ui/ContainerBox';
import {
    Accordion as ExpansionPanel,
    AccordionDetails as ExpansionPanelDetails,
    AccordionSummary as ExpansionPanelSummary,
    Box,
    Breadcrumbs,
    Button,
    Divider,
    Typography,
    Typography as BreadcrumbsText,
} from '@mui/material';
import { TextArea, TextField } from 'components/ui/FormControls';
import React, { ChangeEvent, useEffect, useState } from 'react';

import { FieldAttributes, Form, Formik, useField } from 'formik';
import {
    AlertMessage,
    PromotionalContent,
    PromotionalContentNullable,
} from '../../../shared/types';
import { useApolloClient, useMutation, useQuery } from '@apollo/react-hooks';
import { queryPromotionalContentCache } from './PromotionalContentAddEdit';
import PromotionEditor from '../../ui/admin/PromotionEditor';
import * as Yup from 'yup';
import {
    FormSnackBarType,
    generateSnackBarFormMessage,
    useConstrainTextArea,
} from '../../../utils/form.utils';
import { ManageTemplateInstance } from '../../ui/ManageTemplate';
import { translateErrorMessage } from '../../../shared/error-handling';
import {
    MessageTypes,
    useNotification,
} from '../../providers/NotificationProvider';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { sanitizeRouteId } from '../../../utils/route-params.utils';

import { useHistory, useParams } from 'react-router-dom';
import gql from 'graphql-tag';
import { AlertBox } from 'components/ui/Feedback';

interface BreadcrumbsData {
    contentUrl: string;
    contentTitle: string;
    pageTitle: string;
}

interface SidebarPanelProps {
    label: string;
    children: React.ReactNode;
    templateDetails?: boolean;
}

const SidebarPanel: React.FC<SidebarPanelProps> = ({
    label,
    children,
    templateDetails = false,
}) => {
    return (
        <ExpansionPanel sx={{ overflow: 'visible' }} defaultExpanded={true}>
            <ExpansionPanelSummary>{label}</ExpansionPanelSummary>
            <ExpansionPanelDetails
                sx={{
                    bgcolor: 'background.paper',
                    display: 'block',
                    '&>div:not(:last-child)': {
                        mb: templateDetails ? 1 : 2,
                    },
                }}
            >
                {children}
            </ExpansionPanelDetails>
        </ExpansionPanel>
    );
};

const TextInput: React.FC<
    FieldAttributes<{
        label: string;
        name: string;
        placeholder: string;
        id: string;
    }>
> = ({ label, name, placeholder, id }) => {
    const [{ value, ...field }, meta] = useField({ name });
    return (
        <TextField
            {...field}
            value={value}
            label={label}
            error={meta.touched && !!meta.error}
            errorText={meta.error}
            placeholder={placeholder}
            id={id}
        />
    );
};

const GET_PROMOTION_PAGE = gql`
    query findPromotionPageById($id: ID!) {
        document: findPromotionPageById(id: $id) {
            _id
            name
            html
            teamsChannel
            version
            pageSummary
            socialSharingImageUrl
            successTitle
            successMessage
            promotionalContent {
                _id
            }
        }
    }
`;

const ADD_PROMOTION_PAGE = gql`
    mutation addPromotionPage($item: PromotionPageInputAdd) {
        addPromotionPage(item: $item) {
            _id
        }
    }
`;

const EDIT_PROMOTION_PAGE = gql`
    mutation editPromotionPage($item: PromotionPageInputEdit) {
        editPromotionPage(item: $item) {
            _id
        }
    }
`;

export const PromotionPageAddEdit = (): JSX.Element => {
    const { _id: routeId } = useParams<{ _id?: string }>();
    const _id = sanitizeRouteId(routeId);
    const { showMessage } = useNotification();
    const { push } = useHistory();
    const apolloClient = useApolloClient();
    const [pageMessage, setPageMessage] = useState<AlertMessage>({
        style: 'warning',
        message: null,
    });
    const [isPressSubmit, setIsPressSubmit] = useState(false);
    const pageSummaryTextArea = useConstrainTextArea(0, 200);
    const successMessageTextArea = useConstrainTextArea(0, 200);

    const { data, loading } = useQuery(GET_PROMOTION_PAGE, {
        skip: !_id,
        variables: { id: _id, withTemplate: !!_id },
        fetchPolicy: 'network-only',
        onCompleted: (queryData) => {
            pageSummaryTextArea?.setCurrentChars(
                queryData?.document?.pageSummary?.length || 0
            );
            successMessageTextArea?.setCurrentChars(
                queryData?.document?.successMessage?.length || 0
            );
        },
    });

    const editItem = data?.document;

    const cachedItem = readContentFromCache();

    useEffect(() => {
        const filteredPromotionalContent =
            cachedItem && editItem?.promotionalContent
                ? editItem.promotionalContent.filter(
                      (content: PromotionalContent) =>
                          content._id !== cachedItem._id
                  )
                : [];
        if (filteredPromotionalContent?.length) {
            setPageMessage({
                style: 'warning',
                message:
                    'Heads up! This page is currently in use. Any updates will be reflected everywhere.',
            });
        }
    }, [cachedItem, editItem]);

    const [addPromotionPageAction] = useMutation(ADD_PROMOTION_PAGE);
    const [editPromotionPageAction] = useMutation(EDIT_PROMOTION_PAGE);

    if (loading) return <Loader />;

    const editMode = !!editItem;

    const breadcrumbs = getBreadcrumbsData(cachedItem);

    function readContentFromCache(): PromotionalContentNullable {
        try {
            const readData = apolloClient.readQuery({
                query: queryPromotionalContentCache,
            });
            return readData?.savedItem;
        } catch (ex) {
            return null;
        }
    }

    function updateContentInCache(name: string) {
        if (!cachedItem) return;

        cachedItem['newPageName'] = name;

        apolloClient.writeQuery({
            query: queryPromotionalContentCache,
            data: {
                savedItem: cachedItem,
            },
        });
    }

    function getBreadcrumbsData(
        cached: PromotionalContentNullable
    ): BreadcrumbsData {
        const resultValue: BreadcrumbsData = {
            contentTitle: '',
            contentUrl: baseUrls.publisher.promotionalContent.list,
            pageTitle: editMode ? 'Edit landing page' : 'New landing page',
        };

        if (cached) {
            if (cached._id === '-') {
                resultValue.contentTitle = 'New promotional content';
                resultValue.contentUrl =
                    baseUrls.publisher.promotionalContent.add;
            } else {
                resultValue.contentTitle = 'Edit promotional content';
                resultValue.contentUrl = `${baseUrls.publisher.promotionalContent.list}/edit/${cached?._id}`;
            }
        }

        return resultValue;
    }

    return (
        <Formik
            initialValues={{
                name: editItem?.name || '',
                html: editItem?.html || '',
                teamsChannel: editItem?.teamsChannel || '',
                pageSummary: editItem?.pageSummary || '',
                socialSharingImageUrl: editItem?.socialSharingImageUrl || '',
                successTitle: editItem?.successTitle || '',
                successMessage: editItem?.successMessage || '',
            }}
            onSubmit={async (values, { setSubmitting }) => {
                try {
                    setSubmitting(true);
                    const saveFn = editMode
                        ? editPromotionPageAction
                        : addPromotionPageAction;

                    if (!editMode) {
                        updateContentInCache(values.name);
                    }

                    await saveFn({
                        variables: {
                            item: {
                                _id,
                                name: values.name,
                                html: values.html,
                                teamsChannel: values.teamsChannel,
                                version: editItem?.version || undefined,
                                pageSummary: values.pageSummary,
                                socialSharingImageUrl:
                                    values.socialSharingImageUrl,
                                successTitle: values.successTitle,
                                successMessage: values.successMessage,
                            },
                        },
                    });
                    showMessage(
                        generateSnackBarFormMessage(
                            ManageTemplateInstance.PromotionPage,
                            values.name,
                            editMode
                                ? FormSnackBarType.Updated
                                : FormSnackBarType.Added
                        ),
                        MessageTypes.info
                    );
                    push(breadcrumbs.contentUrl);
                } catch (error) {
                    setSubmitting(false);
                    showMessage(
                        translateErrorMessage(error.message),
                        MessageTypes.error
                    );
                }
            }}
            validationSchema={Yup.object({
                name: Yup.string().required('This field is required.'),
                html: Yup.string().required('This field is required.'),
                teamsChannel: Yup.string().required('This field is required.'),
                pageSummary: Yup.string().required('This field is required.'),
                socialSharingImageUrl: Yup.string().required(
                    'This field is required.'
                ),
                successTitle: Yup.string().required('This field is required.'),
                successMessage: Yup.string().required(
                    'This field is required.'
                ),
            })}
        >
            {({ values, setFieldValue, errors, touched, setTouched }) => (
                <Box component={Form} sx={{ height: '100%' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            width: '100%',
                            height: '100%',
                            pl: 2,
                        }}
                    >
                        <ContainerBox sx={{ px: 0, mt: 2 }}>
                            <Box sx={{ maxWidth: 1200, width: '100%' }}>
                                {cachedItem !== undefined &&
                                cachedItem !== null ? (
                                    <Breadcrumbs>
                                        <Link
                                            to={
                                                baseUrls.publisher
                                                    .promotionalContent.list
                                            }
                                        >
                                            Promotional Content
                                        </Link>
                                        <Link to={breadcrumbs.contentUrl}>
                                            {' '}
                                            {breadcrumbs.contentTitle}
                                        </Link>
                                        <BreadcrumbsText>
                                            {breadcrumbs.pageTitle}
                                        </BreadcrumbsText>
                                    </Breadcrumbs>
                                ) : (
                                    <Breadcrumbs>
                                        <Link
                                            to={
                                                baseUrls.publisher
                                                    .promotionalContent.list
                                            }
                                        >
                                            Promotional Content
                                        </Link>
                                        <BreadcrumbsText>
                                            {breadcrumbs.pageTitle}
                                        </BreadcrumbsText>
                                    </Breadcrumbs>
                                )}

                                <Typography sx={{ mt: 4, mb: 1 }} variant="h5">
                                    Promotional landing page
                                </Typography>
                                <Typography variant="body1">
                                    Add content to build a landing page for
                                    promotional content. Don't forget to add a
                                    main button for the user to register their
                                    interest.
                                </Typography>
                                <Box sx={{ my: 2 }}>
                                    <AlertBox
                                        id="article-add-edit-alert"
                                        visible={!!pageMessage.message}
                                        children={pageMessage.message}
                                        styleName={pageMessage.style}
                                    />
                                </Box>
                                <Box sx={{ mt: 2, maxWidth: 700 }}>
                                    <PromotionEditor
                                        initialHtml={values.html ?? ' '}
                                        errorMessage={
                                            isPressSubmit
                                                ? (errors.html as string)
                                                : undefined
                                        }
                                        onChange={(html) =>
                                            setFieldValue('html', html)
                                        }
                                    />
                                </Box>
                            </Box>
                        </ContainerBox>

                        <Box
                            sx={{
                                bgcolor: 'background.paper',
                                borderLeft: 1,
                                borderColor: 'divider',
                                ml: 2,
                                minWidth: 368,
                                width: 368,
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box
                                sx={{
                                    bgcolor: 'background.paper',
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                }}
                            >
                                <Box
                                    sx={{ p: 2, '&>button': { width: '100%' } }}
                                >
                                    <Button
                                        id="save-page-button"
                                        onClick={() => setIsPressSubmit(true)}
                                        variant="contained"
                                        color="primary"
                                        size="medium"
                                        type="submit"
                                    >
                                        {!editMode
                                            ? 'Save landing page'
                                            : 'Update landing page'}
                                    </Button>
                                </Box>
                            </Box>

                            <Box>
                                <SidebarPanel
                                    label="Template details"
                                    templateDetails
                                >
                                    <TextInput
                                        id="page-name-input"
                                        label="Landing page name"
                                        name="name"
                                        placeholder=""
                                    />
                                    <Typography
                                        variant="caption"
                                        sx={{ display: 'block', mb: 2 }}
                                    >
                                        The page name is visible in the browser
                                        and in the social sharing preview.
                                    </Typography>

                                    <TextInput
                                        id="channel-name-input"
                                        label="Microsoft Teams Group/Channel"
                                        name="teamsChannel"
                                        placeholder="Group/Channel"
                                    />
                                    <Typography variant="caption">
                                        Automatically get notifications to your
                                        chosen Teams channel when a user clicks
                                        on the button on the landing page.
                                    </Typography>
                                </SidebarPanel>
                                <SidebarPanel
                                    label="Success message"
                                    templateDetails
                                >
                                    <TextInput
                                        id="success-title"
                                        label="Title"
                                        name="successTitle"
                                        placeholder="We will be in touch."
                                    />
                                    <Typography
                                        variant="caption"
                                        sx={{ display: 'block', mb: 2 }}
                                    >
                                        Tell the user that they successfully
                                        registered their interest in the
                                        promotion.
                                    </Typography>
                                    <TextArea
                                        id="success-message"
                                        label="Message"
                                        name="successMessage"
                                        placeholder="A member of the desk will be in contact soon."
                                        fullWidth
                                        type="text"
                                        error={
                                            touched.successMessage &&
                                            !!errors.successMessage
                                        }
                                        errorText={
                                            errors.successMessage as string
                                        }
                                        progressBarHelperText={
                                            successMessageTextArea?.charsMessage
                                        }
                                        autoComplete="off"
                                        rowsMax={4}
                                        value={values.successMessage}
                                        showProgressBar={true}
                                        progressBarIndicator={
                                            successMessageTextArea?.currentChars
                                        }
                                        progressBarLimit={
                                            successMessageTextArea?.charsLimit
                                        }
                                        onChange={(
                                            e: ChangeEvent<any>
                                        ): void => {
                                            const val: string =
                                                e?.target?.value;
                                            if (
                                                val?.length >
                                                successMessageTextArea?.charsLimit
                                            ) {
                                                return;
                                            }
                                            successMessageTextArea?.setCurrentChars(
                                                val?.length
                                            );
                                            setFieldValue(
                                                'successMessage',
                                                val
                                            );
                                        }}
                                        onBlur={() =>
                                            setTouched({
                                                ...touched,
                                                successMessage: true,
                                            })
                                        }
                                    />
                                </SidebarPanel>
                                <SidebarPanel
                                    label="Social sharing"
                                    templateDetails
                                >
                                    <TextArea
                                        id="channel-pageSummary-input"
                                        label="Page summary"
                                        name="pageSummary"
                                        placeholder=""
                                        sx={{ mb: 2 }}
                                        fullWidth
                                        type="text"
                                        error={
                                            touched.pageSummary &&
                                            !!errors.pageSummary
                                        }
                                        errorText={errors.pageSummary as string}
                                        progressBarHelperText={
                                            pageSummaryTextArea?.charsMessage
                                        }
                                        autoComplete="off"
                                        rowsMax={4}
                                        value={values.pageSummary}
                                        showProgressBar={true}
                                        progressBarIndicator={
                                            pageSummaryTextArea?.currentChars
                                        }
                                        progressBarLimit={
                                            pageSummaryTextArea?.charsLimit
                                        }
                                        onChange={(
                                            e: ChangeEvent<any>
                                        ): void => {
                                            const val: string =
                                                e?.target?.value;
                                            if (
                                                val?.length >
                                                pageSummaryTextArea?.charsLimit
                                            ) {
                                                return;
                                            }
                                            pageSummaryTextArea?.setCurrentChars(
                                                val?.length
                                            );
                                            setFieldValue('pageSummary', val);
                                        }}
                                        onBlur={() =>
                                            setTouched({
                                                ...touched,
                                                pageSummary: true,
                                            })
                                        }
                                    />
                                    <TextInput
                                        id="page-socialSharingImageUrl-input"
                                        label="Social sharing image URL"
                                        name="socialSharingImageUrl"
                                        placeholder="Image file URL"
                                    />
                                    <Typography variant="caption">
                                        This image will be shown when this page
                                        is shared on any social media platform.
                                        The image will be cropped to a ratio of
                                        2:1.
                                    </Typography>
                                </SidebarPanel>
                            </Box>
                            <Divider />
                        </Box>
                    </Box>
                </Box>
            )}
        </Formik>
    );
};

export default PromotionPageAddEdit;
